
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import { useNavigate } from "react-router-dom";
import { useEffect } from "react";

export type UserProfile = {
  id: string;
  avatar_url: string | null;
  full_name: string | null;
  updated_at: string;
  user_type: string;
  login_type: string | null;
  email?: string;
  ownerData?: {
    business_type_id: string | null;
    company_name: string | null;
    created_at: string;
    currency: string | null;
    owner_id: string;
    business_types?: {
      name: string;
      description: string | null;
    };
  };
};

export const useUserProfile = () => {
  const navigate = useNavigate();

  useEffect(() => {
    const checkSession = async () => {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) {
        console.log("No active session, redirecting to login");
        navigate("/login-owner");
      }
    };
    
    checkSession();
  }, [navigate]);

  return useQuery({
    queryKey: ["currentUser"],
    queryFn: async () => {
      try {
        // Vérifier d'abord si nous avons une session active
        const { data: sessionData, error: sessionError } = await supabase.auth.getSession();
        
        if (sessionError) {
          console.error("Session error:", sessionError);
          navigate("/login-owner");
          throw new Error("Erreur de session");
        }

        if (!sessionData.session) {
          console.log("No active session");
          navigate("/login-owner");
          throw new Error("Aucune session active");
        }

        // Récupérer l'utilisateur authentifié
        const { data: { user }, error: userError } = await supabase.auth.getUser();
        if (userError) {
          console.error("User error:", userError);
          throw userError;
        }
        if (!user) {
          console.error("No user found");
          navigate("/login-owner");
          throw new Error("Utilisateur non trouvé");
        }
        
        console.log("Current user:", user);
        
        // Récupérer le profil
        const { data: profile, error: profileError } = await supabase
          .from("profiles")
          .select("*")
          .eq("id", user.id)
          .single();
        
        if (profileError) {
          console.error("Error fetching profile:", profileError);
          throw profileError;
        }

        if (!profile) {
          console.error("No profile found for user:", user.id);
          throw new Error("Profil non trouvé");
        }

        // Si l'utilisateur est un propriétaire, récupérer les données supplémentaires
        if (profile.user_type === "owner") {
          const { data: ownerData, error: ownerError } = await supabase
            .from("owner_data")
            .select(`
              *,
              business_types!owner_data_business_type_id_fkey (
                name,
                description
              )
            `)
            .eq("owner_id", user.id)
            .maybeSingle();

          if (ownerError) {
            console.error("Error fetching owner data:", ownerError);
            throw ownerError;
          }

          return { 
            ...profile, 
            email: user.email,
            ownerData 
          };
        }
        
        return { ...profile, email: user.email };
      } catch (error: any) {
        console.error("Error in useUserProfile:", error);
        throw error;
      }
    },
    retry: false, // Ne pas réessayer en cas d'erreur
    staleTime: 1000 * 60 * 5, // 5 minutes
  });
};
